import { ItemListAnalyticsModel, LaunchElectronicItemRequest } from '@/@types/client-api';
import { usePlaceRegistrationMutation } from '@/redux/api/client/registration';
import { useCallback } from 'react';
import { makeToastProblemMessage } from './util';
import libraryEndpoints from '@/redux/api/client/library';
import useToast from '../useToast';
import { AnalyticsContext, AnalyticsEventName } from '@/analytics/constants';
import { useAppDispatch } from '..';
import {
  buildJoinWaitlistEventData,
  trackAddToCart,
  trackBeginCheckoutEvent,
  trackPurchaseEvent
} from '@/analytics/ecommerce';
import { useAnalytics } from '@/analytics/hooks/useAnalytics';
import { useFetchAnalyticsItems } from '@/analytics/useFetchAnalyticsItems';
import { createTransactionId } from '@/analytics/analyticsUtils';
import { evictCreditTrackerCode, setCreditTrackerCode } from '../useCreditTrackerCode';
import { setOptimisticRegistrationDetailsUpdate } from '@/redux/slices/searchSlice';
import { Variant } from '@/redux/slices/pdpSlice';
import { useLibraryItems } from '../PDP/useLibraryItems';
import { trackZeroPurchaseValueEvent } from '@/analytics/cards';
import creditPlanEndpoints from '@/redux/api/client/creditPlan';

export const useRegistrations = (
  context?: AnalyticsContext,
  creditTrackerCode?: string,
  isPoecExtensionZeroDollarPurchase?: boolean,
  successToastMessage?: string,
  itemListAnalyticsModel?: ItemListAnalyticsModel
) => {
  const dispatch = useAppDispatch();

  const [registrationMutation, { isLoading, isSuccess, isError, reset, data }] =
    usePlaceRegistrationMutation();
  const { showSuccessMessage, showFailMessage } = useToast();

  const { getAnalyticItem } = useFetchAnalyticsItems();
  const { selectLibraryItem } = useLibraryItems();
  const { trackEvent: trackAnalyticsEvent } = useAnalytics();
  const trackEvent = useCallback(
    async (code: string, context?: AnalyticsContext, isWaitlisted?: boolean) => {
      const data = await getAnalyticItem(code);
      if (context && data) {
        if (isWaitlisted) {
          const eventData = buildJoinWaitlistEventData(data, context, itemListAnalyticsModel);
          if (eventData) trackAnalyticsEvent(AnalyticsEventName.JoinWaitlist, eventData);
          return;
        }

        const transactionId = createTransactionId('');
        trackAddToCart(data, context, transactionId, itemListAnalyticsModel);

        trackBeginCheckoutEvent(data, context, transactionId, itemListAnalyticsModel);
        trackPurchaseEvent(data, context, transactionId, itemListAnalyticsModel);

        const segmentLibraryData = selectLibraryItem(code);
        const alreadyPurchasedLaunchUrl = segmentLibraryData?.watchUrl ?? undefined;
        const isZeroDollar = (!data.price || data.price === 0) && !alreadyPurchasedLaunchUrl;
        if (isZeroDollar) trackZeroPurchaseValueEvent(data, context, itemListAnalyticsModel);
      }
    },
    [getAnalyticItem, selectLibraryItem, itemListAnalyticsModel, trackAnalyticsEvent]
  );

  const placeRegistration = useCallback(
    async (variant: Variant, isUpdate: boolean = false) => {
      if (!variant.code) return;
      const itemPk = variant.code;
      const regWaitlistSuccessMessage = "You've been added to the Waitlist";

      if (itemPk && creditTrackerCode) {
        setCreditTrackerCode(itemPk, creditTrackerCode);
      }

      const regRequest: LaunchElectronicItemRequest = { itemSk: variant.sk, itemPk: itemPk };
      const regFailedMessage = makeToastProblemMessage('register');
      const regUpdateFailedMessage = makeToastProblemMessage('register-update');
      const regSuccessMessage = !successToastMessage ? 'Registered' : successToastMessage;
      const regUpdateSuccessMessage = 'Registration updated';
      const regResponse = await registrationMutation(regRequest);
      if ('error' in regResponse) {
        showFailMessage(isUpdate ? regUpdateFailedMessage : regFailedMessage);
      } else if ('data' in regResponse) {
        const isWaitlisted = regResponse.data?.isWaitlisted || variant.waitlist;
        trackEvent(itemPk, context, isWaitlisted);
        if (variant.sk) evictCreditTrackerCode(itemPk);
        const successMessage = isWaitlisted
          ? regWaitlistSuccessMessage
          : isUpdate
            ? regUpdateSuccessMessage
            : regSuccessMessage;
        showSuccessMessage(successMessage);

        // For POEC extension purchases, also invalidate the target POEC OD program
        if (
          isPoecExtensionZeroDollarPurchase &&
          variant.poecExtensionData?.poecExtensionTargetItem?.pk
        ) {
          dispatch(
            libraryEndpoints.util.invalidateTags([
              { type: 'LibraryItem', id: itemPk },
              { type: 'LibraryItem', id: variant.poecExtensionData.poecExtensionTargetItem.pk },
              { type: 'Library', id: 'List' },
              { type: 'Search', id: 'Results' }
            ])
          );
          dispatch(creditPlanEndpoints.util.invalidateTags(['CreditPlans']));
        } else {
          dispatch(
            libraryEndpoints.util.invalidateTags([
              { type: 'LibraryItem', id: itemPk },
              { type: 'Library', id: 'List' },
              { type: 'Search', id: 'Results' }
            ])
          );
          dispatch(creditPlanEndpoints.util.invalidateTags(['CreditPlans']));
        }

        if (isPoecExtensionZeroDollarPurchase) {
          dispatch(libraryEndpoints.util.invalidateTags([{ type: 'POECRenewalItems' }]));
        }

        dispatch(
          setOptimisticRegistrationDetailsUpdate({
            pk: itemPk,
            isRegistered: true,
            format: variant?.type == 'online' ? 'webcast' : variant?.type,
            location: variant?.location,
            startDate: variant?.eventStartDate,
            endDate: variant?.eventEndDate
          })
        );
      }

      return regResponse;
    },
    [
      trackEvent,
      context,
      creditTrackerCode,
      successToastMessage,
      registrationMutation,
      showFailMessage,
      showSuccessMessage,
      dispatch,
      isPoecExtensionZeroDollarPurchase
    ]
  );

  return {
    placeRegistration,
    isLoading,
    isSuccess,
    isError,
    reset,
    data
  };
};
